import { FC, ReactNode } from 'react';

import {
  closestCenter,
  DndContext,
  DndContextProps,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext } from '@dnd-kit/sortable';
import { isMobile } from '../../helpers/devices';
import { sortOnDragEnd } from './sorter-helpers';

interface Props extends DndContextProps {
  itemIds: UniqueIdentifier[];
  axis?: 'x' | 'y';
  dragElement?: ReactNode;
  onSort?: (data: UniqueIdentifier[]) => void;
  isMobileDisabled?: boolean;
  isDisabled?: boolean;
}

const modifiers = {
  x: [restrictToHorizontalAxis],
  y: [restrictToVerticalAxis],
};

export const Sorter: FC<Props> = (props) => {
  const { children, itemIds, axis, dragElement, onSort, isDisabled } = props;
  const options = { activationConstraint: { distance: 5 } };
  const sensors = useSensors(useSensor(MouseSensor, options), useSensor(TouchSensor, options));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const result = sortOnDragEnd(itemIds, active.id, over?.id);
    onSort?.(result);
  };

  if (isMobile()) return children;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={axis ? modifiers[axis] : undefined}
      onDragEnd={handleDragEnd}
      {...props}
    >
      <SortableContext disabled={isDisabled} items={itemIds}>
        {children}
      </SortableContext>

      <DragOverlay>{dragElement ? dragElement : null}</DragOverlay>
    </DndContext>
  );
};
